export type RelationshipLabel = 'friend' | 'relative' | 'coworker' | 'acquaintance' | 'partner' | 'custom';

/** `{ [targetIdentity]: signalId[] }` — see src/shared/block-signal.ts for the aggregation logic. */
export type ReceivedBlockSignals = Record<string, string[]>;

/** `{ [targetIdentity]: { groupId, signalId, sharedAt } }` — the SENDER's own record of which
 *  group scope they've shared each blocked identity with (a status, not a log — one active
 *  scope per target). See src/shared/block-signal.ts's resolveSharedSignalsForContact. */
export type SharedBlockSignals = Record<string, { groupId: string; signalId: string; sharedAt: string }>;

export interface TalkIntakeFilters {
  /**
   * When enabled, Talk offers and responses may only cross an edge to a user in
   * `knownPeople`. Nearby/chatroom discovery remains available; this only gates
   * Talk exchange. Undefined is treated as false for pre-feature accounts.
   */
  contactsOnlyTalks?: boolean;
  /**
   * "Residents only" Talk audience: no Talk exchange with room members marked as Travelers ✈
   * (a chosen point, not blurred GPS). Contacts are always allowed. Self-declared, so it filters
   * honest Travelers only; it also excludes every website user.
   */
  residentsOnlyTalks?: boolean;
  sentAfter?: string;
  allowedLanguages: string[];
  requireGoodGrammar: boolean;
  blockDirtyWords: boolean;
  allowedTalkTypes: Array<'flow' | 'survey' | 'tag' | 'route'>;
  /** Receiver-defined substrings (normalized server-side) that reject matching talk text. */
  customBlockedTerms?: string[];
  /**
   * Receiver-editable dirty-word list (SEA-private). Merged with ContentFilter's
   * built-in terms when `blockDirtyWords` is enabled. Whole-word matching on
   * NFKC-lowercased text, so "cocktail" does not match "cock". Seeded with
   * DEFAULT_DIRTY_WORDS on first use; empty list + enabled = built-ins only.
   */
  dirtyWords?: string[];
}

export interface KnownPerson {
  userId: string;
  /** Stable SEA identity pinned when this stranger becomes a Contact. */
  identityPub?: string;
  /** Public ECDH identity observed at pin time; retained for durable pair-state migrations. */
  identityEpub?: string;
  identityPinnedAt?: Date;
  /**
   * A contact can belong to more than one relationship group at once (e.g. both
   * "friend" and "coworker") — always non-empty. Legacy single-`label` records
   * (persisted before this field existed) are normalized to a one-element array
   * on read; see `normalizeKnownPersonLabels` in `web-user-service.ts`.
   */
  labels: RelationshipLabel[];
  customLabel?: string;
  nickname?: string;
  rating?: number;
  notes?: string;
  addedAt: Date;
}

export interface User {
  id: string;
  stageName: string;
  headshot?: string;
  profile: QuestionAnswer[];
  reputation: Reputation;
  location: BlurredLocation;
  languages: string[];
  interests: Tag[];
  createdAt: Date;
  lastActive: Date;
  /** SEA signing/encryption public key (hex) */
  pub?: string;
  /** SEA ephemeral public key for ECDH messaging */
  epub?: string;
  knownPeople?: KnownPerson[];
  blockedUserIds?: string[];
  /**
   * Friend-circle block signal tally — `{ [targetIdentity]: signalId[] }`. Populated by
   * pairwise mailbox-encrypted signals from this account's own contacts (see
   * src/shared/block-signal.ts, app.ts's fanOutBlockSignal/ingestBlockSignalFromMailbox).
   * Local-only in spirit (never re-shared), but persisted via the same SEA-private round trip
   * as knownPeople/blockedUserIds so it survives restarts and syncs across linked devices.
   */
  receivedBlockSignals?: ReceivedBlockSignals;
  /**
   * This account's own record of which group scope it has shared each blocked identity with —
   * a status, re-sent to a contact who newly qualifies (added or relabeled into a matching
   * group), not a one-time push. See src/shared/block-signal.ts's resolveSharedSignalsForContact
   * and app.ts's resendSharedBlockSignalsToNewContact.
   */
  sharedBlockSignals?: SharedBlockSignals;
  talkFilters?: TalkIntakeFilters;
  networkRole?: 'root-techsupport';
  supportMuted?: boolean;
}

/** Who may see this profile Q&A on peer/contact surfaces (API applies this for non-owners). */
export type ProfileAttributeVisibility = 'public' | 'contacts_only' | 'private';

export interface QuestionAnswer {
  id: string;
  question: string;
  answer: string;
  isAuto: boolean; // true = auto (chatbot can reuse), false = manual
  answeredAt: Date;
  /** Omit or `public` — legacy rows treated as public. */
  visibility?: ProfileAttributeVisibility;
}

export interface IpfsAttachment {
  cid: string;
  name: string;
  sizeBytes: number;
  mimeType: string;
  enc: 'sea-pair' | 'none';
}

export interface Reputation {
  questionsAnswered: number;
  talksSent: number;
  matchesFound: number;
  friendsCount: number;
  mutualFriendsCount: number;
  likedCount: number;
  dislikedCount: number;
  starRating: number;
  reviewCount: number;
  ageVerified: boolean;
  ageVerificationVotes: number;
  blockCount: number;
  isHidden: boolean; // user can hide reputation display
}

export interface BlurredLocation {
  region: string; // blurred region identifier
  chatrooms: string[]; // chatroom IDs user belongs to
}

export interface GPSCoordinate {
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: Date;
}

export interface Chatroom {
  id: string;
  name: string;
  type: 'global' | 'location' | 'business' | 'custom';
  location?: GPSCoordinate;
  currentUsers: string[];
  businessInfo?: BusinessInfo;
  createdBy?: string;
  createdAt: Date;
  isActive: boolean;
}

export interface BusinessInfo {
  brandName: string;
  address: string;
  coordinates: GPSCoordinate;
  description: string;
  ownerId: string;
  verified: boolean;
}

/**
 * One step in a context path: identifies which question was asked and which
 * answer was selected by the user at that step. Used by route-type talks to
 * distinguish otherwise identical questions that appear in different branches.
 */
export interface ContextStep {
  questionId: string;
  answerId: string;
}

/**
 * A flat answer record stored in the user's answer table.
 * Used by the chatbot to decide whether it can auto-reply.
 *
 * Context is represented as a single hash ID (not a full path list):
 *
 * - tag / survey / flow: contextHash is '' (empty string),
 *   meaning no context is required.
 * - route: contextHash is an 8-char FNV-1a hex hash of the ordered
 *   (questionId:answerId) steps that were active before this question was
 *   shown. The chatbot matches by computing the hash of the current path
 *   and comparing it against stored hashes — O(1) lookup, no list traversal.
 *
 * The full contextPath is NOT stored here; it only lives on the talk
 * definition (Question.contextPath) where it is needed for route traversal.
 */
export interface AnswerWithContext {
  questionId: string;
  answerId: string;
  answerText: string;
  /** Structured source value for a self-authored built-in declaration (§30.3/§EE).
   *  `answerText` remains its display projection for backward-compatible consumers. */
  typedValue?: BuiltInQuestionSpec;
  /**
   * Hash of the context path that was active when this answer was given.
   * '' (empty) for tag/survey/flow. 8-char hex for route branches.
   * Computed by RouteProcessor.buildContextHash().
   */
  contextHash: string;
  visibility: 'auto' | 'manual';
  recordedAt: Date;
}

export interface Talk {
  id: string;
  title: string;
  authorId: string;
  /**
   * The four talk types (see §3.6.1 of the technical specification):
   *
   * - 'tag'    : The Atom of Interest. Single keyword/phrase (Boolean toggle):
   *              checked = match, unchecked = ignore. Isolated node, no context.
   * - 'flow'   : The Linear Thread. Sequential chain of Q/A (path graph).
   *              Every question uses all prior Q/A as context. The chatbot
   *              auto-replies when the full preceding context matches a stored answer.
   * - 'survey' : The Distributed Collection. Independent Q/A pairs (star graph).
   *              No shared context; ideal for aggregate statistics.
   * - 'route'  : The Logical Map. Branching DAG combining flow and survey logic.
   *              Each question carries a contextPath (for construction only).
   *              The chatbot auto-replies only when the stored contextHash matches
   *              the hash of the current conversation path.
   */
  type: 'flow' | 'survey' | 'tag' | 'route';
  isAdult: boolean;
  language: string;
  tags: Tag[];
  questions: Question[];
  ipfsAttachments?: IpfsAttachment[];
  createdAt: Date;
  isTemplate: boolean;
  usageCount: number;
  /** Expiration timestamp (ms). null/undefined = forever. Once expired, talk is not sent automatically but can be re-activated. */
  expiresAt?: number | null;
  /** Location radius in miles. null/undefined = anywhere. Recipients outside this radius are filtered out. */
  locationRadiusMiles?: number | null;
  /** Author's location when talk was created (for distance filtering). */
  authorLocation?: { latitude: number; longitude: number };
  /**
   * The author's public encryption key (SEA epub), attached at creation AFTER the
   * content-hash id is computed (must never perturb content-addressed identity/dedup).
   * Responders pair-encrypt their responses with it; carrying it inside the talk means
   * every delivery path (mesh body, server IN-index, direct send) hands the responder
   * the key material it needs WITHOUT a network lookup — lookups under simultaneous-boot
   * load failed and permanently dropped responses (mass/02's stable 8/11).
   */
  authorEpub?: string;
  /**
   * docs/TODO.md §V — two-author credit model. `authorId`/`createdAt`/`authorLocation`
   * (above) are the *current* editor's identity/timestamp/place — reassigned whenever a
   * content edit (one that changes `questions`) mints a new talk id. These `original*`
   * fields are the *permanent* creator's identity/timestamp/place: set once, on a talk's
   * first edit, seeded from the predecessor's own `original*` fields if it has them,
   * or from its plain `authorId`/`createdAt`/`authorLocation` otherwise (so a talk that
   * predates this field degrades cleanly) — then copied forward unchanged down the
   * `supersedesTalkId` chain. Title-only edits touch none of this (Bernard, 2026-08-01:
   * "changing the title doesn't count as creator or editor").
   */
  originalAuthorId?: string;
  originalCreatedAt?: Date;
  /** Same blurred-by-default shape as `authorLocation` — see docs/TODO.md §X. */
  originalAuthorLocation?: { latitude: number; longitude: number };
  /**
   * Set when a content edit supersedes an earlier talk — the predecessor's id, a
   * provenance pointer only ("new talk can hold a reference to old talk in case that
   * further work is needed", Bernard 2026-08-01), not a functional coupling. The new
   * talk otherwise stands alone as an ordinary independent `Talk`, same shape as the
   * ledger's own `TALK_ANSWERED`-supersedes-prior pattern.
   */
  supersedesTalkId?: string;
  /**
   * Route talks only. When set, switches `checkIfMatch` from the default "check only the
   * terminal answer" rule to a score-threshold rule: every direct child of the route's root is
   * treated as an independent spec (order the author declared them in doesn't matter — see
   * `computeRouteMatchScore`, talk-engine.ts), and the talk matches a responder once the count
   * of that responder's isMatch-flagged spec answers is >= this value. Absent = zero behavior
   * change from today's terminal-only route matching.
   */
  matchThreshold?: number;
  /** Author-only weight used when ranking people inside a match-collection outing. */
  collectionImportance?: 'important' | 'must-have';
}

export interface Question {
  id: string;
  /**
   * CIDv1 content hash of this question's stable content (text + answer ids/texts).
   * Routing-only fields (next, match-flag) are excluded so that routing changes
   * do not invalidate chatbot answer caches keyed by this id.
   * Computed at talk creation/edit time via `computeCIDv1({ text, answers })`.
   * Optional for backward compatibility with talks created before Phase E.
   */
  cidId?: string;
  text: string;
  answers: Answer[];
  nextQuestionId?: string; // for flow (linear) talks
  branchingLogic?: BranchLogic[]; // for route talks
  isAgeGate?: boolean;
  isAggregatable?: boolean; // for surveys
  /**
   * Present on 'route' type questions only.
   * The ordered list of (questionId, answerId) steps that were traversed to
   * reach this question. Two occurrences of the same question text in different
   * branches will have different contextPaths and are stored as separate answers.
   * Empty array means a root question (no prior context).
   */
  contextPath?: ContextStep[];
  /**
   * 8-char FNV-1a hex hash of the preceding (questionId:answerId) chain,
   * or '' when this question has no prior context.
   *
   * - flow   : set on the 2nd question onward (chained from prior Q/A). Q1 is ''.
   * - route  : derived from `contextPath` — set for every question (root = '').
   * - survey : always '' (questions are independent).
   * - tag    : always '' (single isolated question).
   *
   * Computed by `RouteProcessor.buildContextHash` and kept as a denormalized
   * field so the chatbot and UI can look it up without rebuilding the path.
   */
  contextHashId?: string;
  /**
   * Spec §3.4 FR-QA-15 / §30.8: 'single' (default, unchanged radio-button behavior) or
   * 'multiple' ("pick any that apply" — respondent may select more than one option).
   * Undefined/absent means 'single', so every talk created before this field existed is
   * unaffected. See `checkIfMatch`'s set-intersection generalization (FR-QA-16) for how a
   * 'multiple' question's selected set is evaluated against this question's isMatch-flagged
   * options — no boolean/AND-OR vocabulary is ever surfaced to the author or respondent.
   */
  answerSelectionMode?: 'single' | 'multiple';
  /** Author-only weight used by explainable match-collection ranking. */
  collectionImportance?: 'important' | 'must-have';
  /**
   * Spec §30.2/§30.8, TODO.md §BB: marks this question as a typed built-in comparison
   * (quantity/price range/time frame/location) instead of an author-typed text choice.
   * `answers[]` still holds exactly the 2 outcomes flow/route already understand (proceed/
   * ignore) — `TalkAutofix.fix` auto-generates them — but WHICH of the 2 gets picked is
   * computed from typed values (interval-overlap, quantity sufficiency, mutual-containment)
   * rather than chosen by a human or looked up by exact answer text.
   *
   * `location` has no nested lat/long/radius fields: it reuses the talk's own
   * `authorLocation`/`locationRadiusMiles` (already populated at talk creation and already
   * the basis for today's location-based intake filtering) rather than duplicating
   * coordinates onto the question — both sides of a comparison read their own talk's fields.
   */
  builtIn?: BuiltInQuestionSpec;
  /**
   * docs/TODO.md §LL follow-up: when true (only meaningful if this question ends up with
   * exactly one NON-ignore answer — every question needs an Ignore option regardless,
   * `TalkValidator.validateQuestion`), this question's own (text, that one answer's text) pair
   * defines the buy/sell-style tag context ("Pair tag") for every question after it in this
   * branch — the sole mechanism for declaring this now, usable anywhere in a flow/route (the
   * old talk-level `selfTag`/`preferenceSet` root fields have been removed entirely). Read
   * "reversed" (answer text acts as my own tag, question text as the counterpart) when a
   * responder — not this talk's own author — is the one walking through it; see
   * `myEffectiveTagContext` (ui-manager.ts).
   */
  reciprocalTagContext?: boolean;
  /**
   * Marks this question as a "simple tag": exactly one non-ignore answer, whose text MUST equal
   * the question's own text (self-match — "Tennis" matches "Tennis"). Mutually exclusive with
   * `reciprocalTagContext` (that's the asymmetric "Pair tag" case, where the answer is free to
   * differ — see that field's own doc comment). Usable on any question in any talk type
   * (tag/flow/survey/route) — the same atomic unit, not a talk-level-only concept.
   */
  tagKind?: 'simple';
}

/** See `Question.builtIn`. */
export type BuiltInQuestionKind = 'quantity' | 'priceRange' | 'timeFrame' | 'location' | 'ageRange';

export interface BuiltInQuestionSpec {
  kind: BuiltInQuestionKind;
  /** kind === 'quantity': this side's declared quantity (buyer's want / seller's have). */
  quantity?: number;
  /** kind === 'priceRange'. */
  priceRange?: { min: number; max: number };
  /** kind === 'timeFrame'. Epoch milliseconds. */
  timeFrame?: { start: number; end: number };
  /**
   * kind === 'ageRange' (§DD, spec §30.6). Distinct from `priceRange`/`timeFrame`'s plain
   * interval-overlap: one side of the comparison is a single declared fact (this author's own
   * age), the other is a range (their acceptable partner-age range) — see
   * `ageRangeMutuallyAcceptable` (built-in-comparisons.ts) for the actual mutual check.
   */
  ageRange?: { age: number; acceptableRange: { min: number; max: number } };
}

export interface Answer {
  id: string;
  text: string;
  nextQuestionId?: string;
  isTerminal?: boolean; // ends the talk
  isIgnore?: boolean; // filters out user
  isMatch?: boolean; // "Let's talk in person"
  /**
   * Aggregate counter used by 'survey' talks for statistics. Every time a
   * responder picks this answer the counter is incremented. Absent / undefined
   * for non-survey talks. Initialised to 0 when a survey is created.
   */
  counter?: number;
  /**
   * Route talks only — generalizes `Talk.matchThreshold` (previously root-only) to any
   * answer in the tree: this answer fans out into N independent, order-free specs instead
   * of one linear `nextQuestionId`. Lets a route both expand vertically (ordinary chained
   * questions) and horizontally at any node — e.g. an "iPhone" answer fanning out into
   * Model/Condition/Price-range, each answerable in either order by both sides and still
   * matching. Mutually exclusive with `nextQuestionId`: when exactly one child exists the
   * editor still emits the singular field (zero behavior change for every existing route
   * talk); this array is only populated once an author adds a 2nd+ child under one answer.
   * See `evaluateRouteFanOut`/`checkIfMatch` (talk-engine.ts) for how the recursive score is
   * computed, and `parallelMatchThreshold` below for the pass/fail rule at this node.
   */
  nextQuestionIds?: string[];
  /**
   * Only meaningful alongside `nextQuestionIds`. How many of those specs must resolve
   * isMatch for this fan-out node itself to count as "passed." Omitted/undefined defaults
   * to requiring ALL of them — the strictest, most intuitive reading of "these attributes
   * together define compatibility" — mirroring `Talk.matchThreshold`'s existing
   * blank-means-strictest-default convention at the (now legacy, root-only) talk level.
   */
  parallelMatchThreshold?: number;
}

export interface BranchLogic {
  answerId: string;
  nextQuestionId: string;
}

export interface Tag {
  id: string;
  name: string;
  category: TagCategory;
  popularity: number;
  region?: string; // regional popularity
}

export type TagCategory = 
  | 'for-sale'
  | 'housing' 
  | 'services'
  | 'community'
  | 'personals'
  | 'jobs'
  | 'gigs'
  | 'resumes'
  | 'discussion'
  | 'other';

export interface Conversation {
  id: string;
  participants: string[];
  talkId?: string; // if started from a talk
  messages: Message[];
  /** `'withdrawn'` added in step 10: hard retraction by author; read-only both sides. */
  status: 'active' | 'matched' | 'ignored' | 'expired' | 'withdrawn';
  createdAt: Date;
  lastActivity: Date;
  isSurvey: boolean;
  /**
   * Spec §30.2 deal confirmation: participant ids who've explicitly confirmed this deal is
   * final. Only meaningful when the conversation's talk is deal-eligible (declares a Pair-tag
   * question, `isDealEligibleTalk`, app.ts) — a match there isn't exclusive on its own (several
   * compatible candidates can each hold an open conversation), so the talk only disables once
   * BOTH participants appear here.
   */
  dealConfirmedBy?: string[];
  /**
   * Spec §30.2: whether the talk this conversation formed from is deal-eligible (declares a
   * Pair-tag question somewhere in its questions) — written directly onto the conversation
   * record at match time (rather than re-derived per-side from local talk caches, which may not
   * have a matching entry for whichever direction's exchange happened to fire) so the
   * deal-confirmation UI can read it unambiguously from either participant's device.
   */
  dealEligible?: boolean;
  /**
   * Route `matchThreshold` scoring result (see `Talk.matchThreshold`, `computeRouteMatchScore`
   * in talk-engine.ts) — the responder's score and the total number of independent specs asked,
   * computed once at match time. `matchScore / matchTotal` is the percentage shown/sorted-by in
   * the talk owner's "Matched items" list (`renderCreatorReplies`, ui-manager.ts). Absent for
   * conversations formed from anything other than a matchThreshold route.
   */
  matchScore?: number;
  matchTotal?: number;
}

export interface Message {
  id: string;
  senderId: string;
  text: string;
  isFromChatbot: boolean;
  questionId?: string; // if part of a talk flow
  answerId?: string;
  /**
   * Per-matched-talk thread scope (redesign §5): messages carrying a talkId belong
   * to that talk's thread; messages without one (or 'direct') are the pair's DM
   * thread. Same Gun path (`conversations/<id>/messages`) — scoping is a field, so
   * the P2P transport rules (spec §19.4) are untouched.
   */
  talkId?: string;
  timestamp: Date;
  readBy: string[];
  /** Default `public` — other values use SEA with recipient/sender epub */
  channel?: 'public' | 'known' | 'mutual';
  /**
   * Two-writer DAG link (REQ-LEDGER-08): the `id` of the last message from the
   * *other* participant that the sender had observed before composing this
   * message. Enables causal ordering and offline merge without a central
   * sequencer — each writer's message tree is a linked list; together they form
   * a DAG whose merge is deterministic.
   */
  prevSeen?: string;
}

export interface Match {
  id: string;
  userIds: string[];
  talkId: string;
  conversationId: string;
  matchedAt: Date;
  status: 'pending' | 'accepted' | 'declined';
}

export interface Survey {
  id: string;
  talkId: string;
  responses: SurveyResponse[];
  aggregatedResults: SurveyAggregation[];
  createdAt: Date;
}

export interface SurveyResponse {
  id: string;
  responderId: string;
  answers: { questionId: string; answerId: string }[];
  submittedAt: Date;
  isAnonymous: boolean;
}

export interface SurveyAggregation {
  questionId: string;
  answerStats: {
    answerId: string;
    count: number;
    percentage: number;
  }[];
  totalResponses: number;
}

export interface Filter {
  language: boolean;
  grammar: boolean;
  dirtyWords: boolean;
  /** Optional — only required when location-based intake filtering is active. */
  location?: {
    enabled: boolean;
    maxDistance: number; // in km
  };
  /** Optional — only required when age-based intake filtering is active. */
  age?: {
    enabled: boolean;
    minAge: number;
    maxAge: number;
  };
}

// Event types for Gun.js real-time updates
export interface UserEvent {
  type: 'user.updated' | 'user.status' | 'user.location';
  userId: string;
  data: any;
  timestamp: Date;
}

export interface ChatroomEvent {
  type: 'chatroom.join' | 'chatroom.leave' | 'chatroom.split' | 'chatroom.message';
  chatroomId: string;
  userId?: string;
  data: any;
  timestamp: Date;
}

export interface ConversationEvent {
  type: 'message.sent' | 'message.read' | 'talk.started' | 'talk.completed' | 'match.created';
  conversationId: string;
  userId?: string;
  data: any;
  timestamp: Date;
}

// ─── Interaction Ledger (Phase E) ─────────────────────────────────────────────

/**
 * Kinds of interaction events recorded in the ledger.
 * Each kind maps to a distinct ledger entry with its own content schema.
 */
export enum InteractionKind {
  TALK_CREATED       = 'TALK_CREATED',
  TALK_BROADCAST     = 'TALK_BROADCAST',
  TALK_RECEIVED      = 'TALK_RECEIVED',
  TALK_ANSWERED      = 'TALK_ANSWERED',
  TALK_SUPERSEDED    = 'TALK_SUPERSEDED',
  TALK_WITHDRAWN     = 'TALK_WITHDRAWN',
  /** Step 10 — hard retraction: author deleted or unchecked the talk; tombstone for all holders. */
  TALK_RETRACTED     = 'TALK_RETRACTED',
  MATCH_CREATED      = 'MATCH_CREATED',
  CONVERSATION_MSG   = 'CONVERSATION_MSG',
  /**
   * TODO §S (docs/design/section-s-merkle-checkpoint-pruning-design-note.md): a signed
   * Merkle checkpoint over the preceding LEDGER_CHECKPOINT_INTERVAL events' CIDv1 ids.
   * Written as a first-class chained event (its own seq + prev), not a side channel, so
   * it inherits the same signature/chain-integrity guarantees as every other event.
   */
  CHECKPOINT_CREATED = 'CHECKPOINT_CREATED',
}

/**
 * A single event in a user's interaction ledger feed.
 *
 * - `id`        CIDv1 of the canonical JSON of all other fields (self-certifying)
 * - `seq`       Monotonically increasing sequence number within this user's feed
 * - `prev`      CIDv1 of the previous event (`null` for the first event)
 * - `kind`      One of InteractionKind
 * - `pubkey`    SEA public key of the event author (for signature verification)
 * - `timestamp` ISO-8601 UTC string at event creation time
 * - `content`   Kind-specific payload (see per-kind type guards below)
 * - `sig`       SEA signature over `canonicalSerialize({ id, seq, prev, kind, pubkey, timestamp, content })`
 */
export interface InteractionEvent {
  id: string;          // CIDv1
  seq: number;
  prev: string | null; // CIDv1 of preceding event, null for genesis
  kind: InteractionKind;
  pubkey: string;
  timestamp: string;   // ISO-8601 UTC
  content: InteractionEventContent;
  sig: string;         // SEA signature
}

/** Union of all per-kind content shapes */
export type InteractionEventContent =
  | TalkCreatedContent
  | TalkBroadcastContent
  | TalkReceivedContent
  | TalkAnsweredContent
  | TalkSupersededContent
  | TalkWithdrawnContent
  | MatchCreatedContent
  | ConversationMsgContent
  | CheckpointCreatedContent;

export interface TalkCreatedContent {
  talkId: string;        // CIDv1 of the talk definition
  title: string;
  type: 'flow' | 'tag' | 'survey' | 'route';
  language: string;
}

export interface TalkBroadcastContent {
  talkId: string;
  recipientCount: number;
  chatroomIds: string[];
}

export interface TalkReceivedContent {
  talkId: string;
  senderId: string;
}

export interface TalkAnsweredContent {
  talkId: string;
  responseId: string;   // CIDv1 of { talkId, responderId, responseContentJson }
  outcome: 'match' | 'mismatch' | 'ignore';
}

export interface TalkSupersededContent {
  oldTalkId: string;
  newTalkId: string;
}

export interface TalkWithdrawnContent {
  talkId: string;
  gracePeriodMs: number;
}

export interface MatchCreatedContent {
  talkId: string;
  conversationId: string;
  otherUserId: string;
}

export interface ConversationMsgContent {
  conversationId: string;
  messageId: string;    // CIDv1 of { conversationId, senderPubkey, content, seq }
  seq: number;
}

/**
 * TODO §S: SRS §28.9.2's checkpoint content. `leafIds` is the full sorted set of event ids
 * this checkpoint commits to — the Merkle root alone cannot regenerate a lost leaf id, so
 * the leaf array must be retained here for a proof to remain buildable after the covered
 * events are pruned (see the design note's Item 3 risk notes; this changes SRS §9.5's own
 * ~256B checkpoint-size estimate, which didn't budget for it).
 */
export interface CheckpointCreatedContent {
  rangeStart: number;
  rangeEnd: number;
  merkleRoot: string;
  count: number;
  leafIds: string[];
}

/**
 * Ledger state snapshot used for delta-sync handshake (REQ-LEDGER-06).
 * Maps userId → highest seq number this peer holds for that user's feed.
 * Peers send only events with seq > the declared value.
 */
export type LedgerState = Record<string, number>;
